'use client'

/**
 * The document's published hooks (ADR 0012).
 *
 * At this commit that is the write side, `useDocumentOps()`. `useDocumentView()` and
 * `useDocumentStatus()` join it in #374, and `useDocumentSelector` travels with the query hooks in
 * step 2 — publishing it here would be a face with no caller, which is the defect #156 records
 * rather than one to repeat.
 */

import { useEffect, useMemo, useRef } from 'react'

import {
  useApplyChange,
  useDocumentUrl,
  type ApplyChange,
} from './internal/change-doc'
import { ensureSchemaVersion } from './migration/orchestrator'
import { operations, type Operations } from './operations'
import type { AppDocument } from './schema'
import type { DocumentView } from './view'

/**
 * An operation with its document argument already supplied.
 *
 * The operation's own return value passes through, which is how a caller gets the id of what it just
 * created without reading the document back.
 */
type Bound<Op> = Op extends (doc: AppDocument, ...args: infer A) => infer R
  ? (...args: A) => R
  : never

export type DocumentOps = { [K in keyof Operations]: Bound<Operations[K]> }

/**
 * Bind one operation to an applier.
 *
 * The change callback runs synchronously, so the operation's return value is available by the time
 * `apply` returns. When the document is not ready the applier does nothing and the result stays
 * `undefined` — which is what every write site already assumed, since the handle this replaces
 * was a no-op before the handle resolved rather than absent.
 */
function bind<A extends unknown[], R>(
  operation: (doc: AppDocument, ...args: A) => R,
  apply: ApplyChange,
): (...args: A) => R {
  return (...args: A): R => {
    let result!: R
    apply((doc) => {
      result = operation(doc, ...args)
    })
    return result
  }
}

/**
 * The document's write operations, bound to this device's document.
 *
 * One call is one change transaction, which is why an operation that has to be atomic with another —
 * an entry and its person links, a media entry and the genre topics it needs — is one operation
 * taking both, rather than two calls.
 */
export function useDocumentOps(): DocumentOps {
  const docUrl = useDocumentUrl()
  const { apply } = useApplyChange(docUrl)

  return useMemo(() => {
    const bound = {} as Record<string, unknown>
    for (const [name, operation] of Object.entries(operations)) {
      bound[name] = bind(
        operation as (doc: AppDocument, ...args: unknown[]) => unknown,
        apply,
      )
    }
    return bound as DocumentOps
  }, [apply])
}

/**
 * Stamp this device's document with the current schema version, once, when it is ready.
 *
 * Published because the stamp is the document's own business while the applier that performs it is
 * not something a caller outside the domain may hold (ADR 0012 rule 2). It was an effect inside
 * `automerge/hooks.ts` until #289; the effect is unchanged, only its side of the seam.
 *
 * "Ready" is the same test that hook used: the document exists and carries its five entity maps,
 * which an empty document for a new user does too.
 */
export function useEnsureSchemaVersion(): void {
  const docUrl = useDocumentUrl()
  const { doc, apply } = useApplyChange(docUrl)
  const stamped = useRef(false)

  useEffect(() => {
    if (!doc || typeof window === 'undefined') return
    if (!isDocumentReady(doc)) return
    if (stamped.current) return

    stamped.current = true
    ensureSchemaVersion(doc, apply)
  }, [doc, apply])
}

/**
 * Whether the document has finished loading its containers.
 *
 * A document with entries is ready by definition; an empty one is ready when all five entity maps
 * exist, which distinguishes a new user's document from a half-loaded handle.
 */
function isDocumentReady(doc: AppDocument): boolean {
  if (typeof doc.entries !== 'object') return false
  if (Object.keys(doc.entries).length > 0) return true

  return (
    typeof doc.topics === 'object' &&
    typeof doc.areas === 'object' &&
    typeof doc.projects === 'object' &&
    typeof doc.links === 'object'
  )
}

/**
 * The document, for reading.
 *
 * `null` until the handle resolves. Returns the same object `useDocumentOps()` writes through, so a
 * component reading here and writing there sees its own write on the next render.
 *
 * It replaces `useAutomergeAdapter().doc` at 49 sites. The two members that hook also published and
 * this one does not are `adapter` and `docUrl`: both had zero consumers, and the adapter behind the
 * first is deleted (#374).
 */
export function useDocumentView(): DocumentView | null {
  const docUrl = useDocumentUrl()
  const { doc } = useApplyChange(docUrl)

  return doc ?? null
}

/**
 * Whether the document has loaded.
 *
 * The same test `useAutomergeAdapter().ready` performed — the handle has resolved — so a caller
 * that gated on it gates on the same thing. It is deliberately not `isDocumentReady` from the
 * schema-version effect above: that one asks whether the containers exist, which is a different and
 * stricter question that no call site was asking.
 */
export function useDocumentStatus(): boolean {
  return useDocumentView() !== null
}
